/**
 * Input component for text-based form inputs
 *
 * Styled with Tailwind CSS using glass morphism for dark theme.
 * Supports validation states (error, success, warning).
 */

import type { InputOptions, ValidationState } from '../types/form';

/**
 * Base CSS classes for the input element
 */
const BASE_CLASSES = [
  'w-full',
  'px-3',
  'py-2',
  'touch-input',
  'text-sm',
  'text-foreground',
  'placeholder:text-muted-foreground',
  'rounded-md',
  'border',
  'bg-neutral-950/60',
  'backdrop-blur-sm',
  'transition-colors',
  'duration-200',
  'focus:outline-none',
  'focus:ring-2',
  'focus:ring-offset-2',
  'focus:ring-offset-background',
  'disabled:cursor-not-allowed',
  'disabled:opacity-50',
].join(' ');

/**
 * CSS classes for validation states
 */
const STATE_CLASSES: Record<ValidationState, string> = {
  default:
    'border-neutral-800 hover:border-neutral-700 focus:border-primary focus:ring-primary/20',
  error:
    'border-destructive hover:border-destructive focus:border-destructive focus:ring-destructive/20',
  success:
    'border-success hover:border-success focus:border-success focus:ring-success/20',
  warning:
    'border-warning hover:border-warning focus:border-warning focus:ring-warning/20',
};

/**
 * Generate a unique ID if not provided
 */
function generateId(): string {
  return `input-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Create an Input element
 */
export function createInput(options: InputOptions = {}): HTMLInputElement {
  const {
    id = generateId(),
    name,
    type = 'text',
    value = '',
    placeholder,
    disabled = false,
    required = false,
    readonly = false,
    min,
    max,
    step,
    minLength,
    maxLength,
    pattern,
    autocomplete,
    autofocus = false,
    className = '',
    validationState = 'default',
    attributes = {},
    onInput,
    onChange,
    onFocus,
    onBlur,
    onKeyDown,
  } = options;

  const input = document.createElement('input');

  // Set basic attributes
  input.type = type;
  input.id = id;
  input.value = value;

  if (name) input.name = name;
  if (placeholder) input.placeholder = placeholder;
  if (disabled) input.disabled = true;
  if (required) input.required = true;
  if (readonly) input.readOnly = true;
  if (autofocus) input.autofocus = true;

  // Number-specific attributes
  if (min !== undefined) input.min = String(min);
  if (max !== undefined) input.max = String(max);
  if (step !== undefined) input.step = String(step);

  // Text-specific attributes
  if (minLength !== undefined) input.minLength = minLength;
  if (maxLength !== undefined) input.maxLength = maxLength;
  if (pattern) input.pattern = pattern;
  if (autocomplete) input.setAttribute('autocomplete', autocomplete);

  // Set CSS classes
  const stateClasses = STATE_CLASSES[validationState];
  input.className = `${BASE_CLASSES} ${stateClasses} ${className}`.trim();

  // Set additional attributes
  for (const [key, val] of Object.entries(attributes)) {
    input.setAttribute(key, val);
  }

  // Attach event handlers
  if (onInput) {
    input.addEventListener('input', (event) => {
      onInput(input.value, event);
    });
  }

  if (onChange) {
    input.addEventListener('change', (event) => {
      onChange(input.value, event);
    });
  }

  if (onFocus) {
    input.addEventListener('focus', (event) => {
      onFocus(event as FocusEvent);
    });
  }

  if (onBlur) {
    input.addEventListener('blur', (event) => {
      onBlur(event as FocusEvent);
    });
  }

  if (onKeyDown) {
    input.addEventListener('keydown', (event) => {
      onKeyDown(event as KeyboardEvent);
    });
  }

  return input;
}

/**
 * Input class for object-oriented usage
 */
export class Input {
  private element: HTMLInputElement;
  private options: InputOptions;

  constructor(options: InputOptions = {}) {
    this.options = { ...options };
    this.element = createInput(this.options);
  }

  /**
   * Get the input element
   */
  getElement(): HTMLInputElement {
    return this.element;
  }

  /**
   * Get the current value
   */
  getValue(): string {
    return this.element.value;
  }

  /**
   * Set the value
   */
  setValue(value: string): this {
    this.element.value = value;
    return this;
  }

  /**
   * Set the validation state
   */
  setValidationState(state: ValidationState): this {
    this.options.validationState = state;
    const stateClasses = STATE_CLASSES[state];

    // Remove all state classes
    Object.values(STATE_CLASSES).forEach((classes) => {
      classes.split(' ').forEach((cls) => {
        this.element.classList.remove(cls);
      });
    });

    // Add new state classes
    stateClasses.split(' ').forEach((cls) => {
      this.element.classList.add(cls);
    });

    return this;
  }

  /**
   * Set disabled state
   */
  setDisabled(disabled: boolean): this {
    this.element.disabled = disabled;
    return this;
  }

  /**
   * Check if disabled
   */
  isDisabled(): boolean {
    return this.element.disabled;
  }

  /**
   * Focus the input
   */
  focus(): this {
    this.element.focus();
    return this;
  }

  /**
   * Blur the input
   */
  blur(): this {
    this.element.blur();
    return this;
  }

  /**
   * Select all text
   */
  select(): this {
    this.element.select();
    return this;
  }

  /**
   * Check validity
   */
  checkValidity(): boolean {
    return this.element.checkValidity();
  }

  /**
   * Report validity (shows browser validation message)
   */
  reportValidity(): boolean {
    return this.element.reportValidity();
  }

  /**
   * Set custom validity message
   */
  setCustomValidity(message: string): this {
    this.element.setCustomValidity(message);
    return this;
  }

  /**
   * Get validation message
   */
  getValidationMessage(): string {
    return this.element.validationMessage;
  }

  /**
   * Clear the input
   */
  clear(): this {
    this.element.value = '';
    return this;
  }

  /**
   * Add event listener
   */
  on<K extends keyof HTMLElementEventMap>(
    type: K,
    listener: (event: HTMLElementEventMap[K]) => void
  ): this {
    this.element.addEventListener(type, listener);
    return this;
  }

  /**
   * Remove event listener
   */
  off<K extends keyof HTMLElementEventMap>(
    type: K,
    listener: (event: HTMLElementEventMap[K]) => void
  ): this {
    this.element.removeEventListener(type, listener);
    return this;
  }
}
